import React, { useState } from "react";
import { Modal, Button, Table, Badge, Card, Form } from "react-bootstrap";
import { formatDate } from "../../utils/helpers";
import currency from "currency.js";
import moment from "moment";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";

const TransactionDetailsModal = ({ transaction, show, onHide, refetch }) => {
  const [newFeedback, setNewFeedback] = useState("");
  const [notify, setNotify] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addFeedback, setAddFeedback] = useState(false);
  const { backendUrl, user } = useAuth();

  // Extract feedback from transaction items if available
  const feedbackItems = transaction?.feedback || [];

  const handleSubmitFeedback = async () => {
    if (!addFeedback) return setAddFeedback(true);
    if (!newFeedback.trim() ) {
      toast.error("Please provide a valid feedback");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/permits/transaction-feedback`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "Application/json",
            "Content-Type": "Application/json",
          },
          body: JSON.stringify({
            name: user.Name || user.username || user.Staff_ID || "Staff",
            feedback: newFeedback,
            transactionId: transaction?.TransactionID,
            isStaff: true,
            notify,
            customerEmail: transaction.customer?.Email,
            customerName: transaction.customer?.LastName || transaction.customer?.FirtstName || transaction.customer?.MiddleName || "Customer"
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit feedback");
      }

      const result = await response.json();
      toast.success(result.message || "Feedback submitted successfully");

      // Reset form
      setNewFeedback("");
      setNotify(false);
      setAddFeedback(false);
      onHide()
      refetch();
    } catch (error) {
      toast.error(`Error submitting feedback: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format delivery information if available
  const deliveryInfo = transaction?.DeliveryInformation
    ? JSON.parse(transaction.DeliveryInformation)[0]
    : null;

  return (
    <Modal
      show={show}
      onHide={() => onHide(false)}
      size="lg"
      aria-labelledby="transaction-details-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="transaction-details-modal">
          Transaction Details
          <Badge
            bg={
              transaction?.Status === "Pending Loading Order"
                ? "warning"
                : "success"
            }
            className="ms-2"
          >
            {transaction?.Status}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="transaction-summary mb-4">
          <h5>Transaction Summary</h5>
          <Table responsive bordered hover>
            <tbody>
              <tr>
                <td className="fw-bold" width="30%">
                  Transaction ID
                </td>
                <td>{transaction?.TransactionID}</td>
              </tr>
              <tr>
                <td className="fw-bold">Date</td>
                <td>{formatDate(transaction?.Date_Log)}</td>
              </tr>
              <tr>
                <td className="fw-bold">Status</td>
                <td>{transaction?.Status}</td>
              </tr>
            </tbody>
          </Table>
        </div>

        {deliveryInfo && (
          <div className="delivery-info mb-4">
            <h5>Delivery Information</h5>
            <Table responsive bordered hover>
              <tbody>
                {deliveryInfo.DriverName && (
                  <tr>
                    <td className="fw-bold" width="30%">
                      Driver
                    </td>
                    <td>{deliveryInfo.DriverName}</td>
                  </tr>
                )}
                {deliveryInfo.TruckNumber && (
                  <tr>
                    <td className="fw-bold">Truck Number</td>
                    <td>{deliveryInfo.TruckNumber}</td>
                  </tr>
                )}
                {deliveryInfo.Volume > 0 && (
                  <tr>
                    <td className="fw-bold">Volume Allocated</td>
                    <td>{deliveryInfo.Volume}</td>
                  </tr>
                )}
                {deliveryInfo.VolumeLoaded > 0 && (
                  <tr>
                    <td className="fw-bold">Volume Loaded</td>
                    <td>{deliveryInfo.VolumeLoaded}</td>
                  </tr>
                )}
                {deliveryInfo.VolumeSupplied > 0 && (
                  <tr>
                    <td className="fw-bold">Volume Supplied</td>
                    <td>{deliveryInfo.VolumeSupplied}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}

        <div className="items-info mb-4">
          <h5>Items</h5>
          <Table responsive bordered hover>
            <thead>
              <tr>
                <th>Item</th>
                <th>Description</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {transaction?.items?.map((item, index) => (
                <tr key={index}>
                  <td>{item.Item_Name}</td>
                  <td>{item.Serial_Number}</td>
                  <td>{item.QTY}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <div className="feedback-section mb-4">
          <h5>Feedbacks</h5>
          {feedbackItems.length > 0 ? (
            <div className="feedback-list">
              <Card className="mb-2">
                {feedbackItems.map((feedback, index) => (
                  <Card.Body key={index} className="d-flex">
                    <div className="me-3">
                      <div className="">
                        <img
                          src={`https://placehold.co/25x25/007bff/ffffff?text=${feedback.feedbackName
                            .charAt(0)
                            .toUpperCase()}`}
                          alt={feedback.feedbackName}
                          className="rounded-circle"
                        />
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <Card.Title className="d-flex justify-content-between">
                        <span className="position-relative">
                          <Badge
                            bg={feedback.isStaff ? "primary" : "success"}
                            className="position-absolute bottom-0"
                            style={{left: "120%"}}
                          >
                            {feedback.isStaff ? "Staff" : "Customer"}
                          </Badge>
                          {feedback.feedbackName}
                        </span>
                        <small className="text-muted">
                          {moment(feedback.feedbackDate).format(
                            "MMM D, YYYY h:mm A"
                          )}
                        </small>
                      </Card.Title>
                      <Card.Text>{feedback.feedback}</Card.Text>
                    </div>
                  </Card.Body>
                ))}
              </Card>
            </div>
          ) : (
            <p className="text-muted">No feedback has been provided yet.</p>
          )}
        </div>

        {addFeedback && (
          <div className="add-feedback-section">
            <h5>Add Your Feedback</h5>
            <Form>
              {/* <Form.Group className="mb-3">
                <Form.Label>Your Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your name"
                  value={feedbackName}
                  onChange={(e) => setFeedbackName(e.target.value)}
                  disabled={isSubmitting}
                />
              </Form.Group> */}
              <Form.Group className="mb-3">
                <Form.Label>Your Feedback</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Share your experience with this transaction"
                  value={newFeedback}
                  onChange={(e) => setNewFeedback(e.target.value)}
                  disabled={isSubmitting}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check 
                  type="checkbox"
                  id="notifyCustomerCheck"
                  label="Notify customer"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                  disabled={isSubmitting}
                />
              </Form.Group>

            </Form>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button variant="secondary" onClick={() => onHide(false)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmitFeedback}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Submitting...
            </>
          ) : addFeedback ? (
            "Submit Feedback"
          ) : (
            "Add Feedback"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransactionDetailsModal;
